import { Box } from "@mui/material";
import { useDrop } from "react-dnd";
import { RequestParticipant } from "models/interfaces";
import { updateMultipleSigners, updateSignerOrders } from "redux/appSlice";
import { normalizeOrders } from "utils/signerOrderUtils";
import approversAndSignersStyles from "../ApproversAndSigners.styles";
import recipientsTokens from "../ApproversAndSigners.tokens";

const styles = approversAndSignersStyles;
const { newLevelZone } = recipientsTokens;

const ItemType = { SIGNER: "signer" };

interface SignerNewLevelZoneProps {
  signers: RequestParticipant[];
  dispatch: any;
}

/**
 * "Drop here to create a new level" — appears only while a signer is being
 * dragged, and moves that signer into a fresh level after the last one.
 */
const SignerNewLevelZone = ({
  signers,
  dispatch,
}: SignerNewLevelZoneProps) => {
  const [{ dragging, isOver }, drop] = useDrop({
    accept: ItemType.SIGNER,
    collect: (monitor) => ({
      dragging: Boolean(monitor.getItem()),
      isOver: monitor.isOver(),
    }),
    drop: (dragged: any) => {
      const signerId = dragged.id ?? dragged.signerId;
      if (signerId === undefined) return;

      const source = signers.find((s) => s.id === signerId);
      if (!source) return;

      const lastOrder = Math.max(
        ...signers.map((s) => Number(s.order) || 0),
        0,
      );
      // A signer that is already alone in the last level has nowhere to go.
      const siblings = signers.filter(
        (s) => Number(s.order) === Number(source.order) && s.id !== source.id,
      );
      if (Number(source.order) === lastOrder && siblings.length === 0) return;

      const normalized = normalizeOrders(
        signers.map((s) =>
          s.id === signerId ? { ...s, order: lastOrder + 1 } : s,
        ),
      );

      // Level 1 can never hold a delay, so clear it if the shuffle lands there.
      const clears = normalized
        .filter(
          (s) => s.order === 1 && (s.delayDays || s.delayHours || s.scheduleTime),
        )
        .map((s) => ({
          signerId: s.id,
          fieldsToUpdate: {
            delayDays: undefined,
            delayHours: undefined,
            scheduleTime: undefined,
          },
        }));
      if (clears.length) dispatch(updateMultipleSigners(clears));
      dispatch(updateSignerOrders(normalized));
    },
  });

  if (!dragging) return null;

  return (
    <Box ref={drop} sx={styles.newLevelZone(isOver)}>
      {newLevelZone.label}
    </Box>
  );
};

export default SignerNewLevelZone;
